'use client';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/app/components/ui/tooltip';
import { cn } from '@/app/lib/utils';
import { VoteType } from '@prisma/client';
import { motion, useAnimation } from 'framer-motion';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useSession } from 'next-auth/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';

type UniversalVoteButtonsProps = {
  votes?: { userId: string; type: VoteType }[];
  likes: number;
  dislikes: number;
  onVote: (voteType: VoteType) => void;
  variant?: 'card' | 'modal';

  /** ✅ если передали — это источник истины */
  myVote?: VoteType | null;
};

export function UniversalVoteButtons({
  votes = [],
  likes,
  dislikes,
  onVote,
  variant = 'modal',
  myVote,
}: UniversalVoteButtonsProps) {
  const { data: session } = useSession();
  const userId = session?.user?.id;
  const [isVoting, setIsVoting] = useState(false);

  /**
   * ✅ ЕДИНЫЙ ИСТОЧНИК АКТИВНОСТИ:
   * 1) myVote (приоритет)
   * 2) fallback на votes (если myVote не передали)
   */
  const resolvedUserVote: VoteType | null = useMemo(() => {
    if (myVote !== undefined) return myVote; // важно: undefined = не передали
    if (!userId) return null;

    const v = votes.find((x) => x.userId === userId);
    return v?.type ?? null;
  }, [myVote, userId, votes]);

  // 🎬 Анимации
  const likeControls = useAnimation();
  const dislikeControls = useAnimation();
  const prevVoteTypeRef = useRef<VoteType | null>(null);

  useEffect(() => {
    if (!resolvedUserVote) {
      prevVoteTypeRef.current = null;
      return;
    }

    if (resolvedUserVote === 'LIKE' && prevVoteTypeRef.current !== 'LIKE') {
      likeControls.start({ scale: [1, 1.4, 1], transition: { duration: 0.25 } });
    }

    if (resolvedUserVote === 'DISLIKE' && prevVoteTypeRef.current !== 'DISLIKE') {
      dislikeControls.start({ scale: [1, 1.4, 1], transition: { duration: 0.25 } });
    }

    prevVoteTypeRef.current = resolvedUserVote;
  }, [resolvedUserVote, likeControls, dislikeControls]);

  const handleVote = useCallback(
    async (voteType: VoteType) => {
      if (!userId) {
        toast.error('Для голосования нужно войти в аккаунт');
        return;
      }

      // Блокируем частые клики (спам)
      if (isVoting) return;
      setIsVoting(true);

      // Снимаем блок через 500мс
      setTimeout(() => setIsVoting(false), 500);

      onVote(voteType);
    },
    [userId, onVote, isVoting],
  );

  const { likeTooltip, dislikeTooltip } = useMemo(() => {
    let like = 'Нравится';
    let dislike = 'Не нравится';

    if (resolvedUserVote === VoteType.LIKE) like = 'Отменить лайк';
    if (resolvedUserVote === VoteType.DISLIKE) dislike = 'Отменить дизлайк';

    return { likeTooltip: like, dislikeTooltip: dislike };
  }, [resolvedUserVote]);

  const isCard = variant === 'card';

  return (
    <div
      className={cn(
        'flex items-center gap-1',
        isCard
          ? 'p-0'
          : 'bg-slate-800/80 p-1.5 rounded-full backdrop-blur-sm border border-slate-700/50',
      )}
    >
      <TooltipProvider delayDuration={100}>
        {/* 👍 LIKE */}
        <Tooltip>
          <TooltipTrigger asChild>
            <motion.button
              type="button"
              onClick={() => handleVote(VoteType.LIKE)}
              whileTap={{ scale: 0.9 }}
              className={cn(
                'flex items-center gap-2 rounded-full transition-all duration-200 focus:outline-none',
                isCard ? 'px-2 py-1' : 'px-3 py-1.5',
                resolvedUserVote === 'LIKE'
                  ? 'bg-sky-500/20 hover:bg-sky-500/30'
                  : 'hover:bg-slate-700',
              )}
            >
              <motion.div animate={likeControls}>
                <ThumbsUp
                  size={isCard ? 16 : 18}
                  className={cn(
                    'transition-colors',
                    resolvedUserVote === 'LIKE' ? 'text-sky-400' : 'text-slate-400',
                  )}
                  fill={resolvedUserVote === 'LIKE' ? 'currentColor' : 'none'}
                />
              </motion.div>

              <span
                className={cn(
                  'font-bold text-sm',
                  resolvedUserVote === 'LIKE' ? 'text-sky-100' : 'text-slate-300',
                )}
              >
                {likes}
              </span>
            </motion.button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{likeTooltip}</p>
          </TooltipContent>
        </Tooltip>

        <div className="w-px h-5 bg-slate-600/50 mx-1" />

        {/* 👎 DISLIKE */}
        <Tooltip>
          <TooltipTrigger asChild>
            <motion.button
              type="button"
              onClick={() => handleVote(VoteType.DISLIKE)}
              whileTap={{ scale: 0.9 }}
              className={cn(
                'flex items-center gap-2 rounded-full transition-all duration-200 focus:outline-none',
                isCard ? 'px-2 py-1' : 'px-3 py-1.5',
                resolvedUserVote === 'DISLIKE'
                  ? 'bg-rose-500/20 hover:bg-rose-500/30'
                  : 'hover:bg-slate-700',
              )}
            >
              <motion.div animate={dislikeControls}>
                <ThumbsDown
                  size={isCard ? 16 : 18}
                  className={cn(
                    'transition-colors',
                    resolvedUserVote === 'DISLIKE' ? 'text-rose-400' : 'text-slate-400',
                  )}
                  fill={resolvedUserVote === 'DISLIKE' ? 'currentColor' : 'none'}
                />
              </motion.div>

              <span
                className={cn(
                  'font-bold text-sm',
                  resolvedUserVote === 'DISLIKE' ? 'text-rose-100' : 'text-slate-300',
                )}
              >
                {dislikes}
              </span>
            </motion.button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{dislikeTooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
